import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const containerStyle = { maxWidth: 900, margin: '40px auto', padding: 24, border: '1px solid #ddd', borderRadius: 10, background: '#fafafa' };
const inputStyle = { width: '100%', padding: 10, marginBottom: 8, boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: 6, fontSize: 14 };
const labelStyle = { display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 14 };
const btnPrimary = { padding: '10px 16px', background: '#007BFF', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', marginRight: 8 };
const btnSecondary = { padding: '10px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', marginRight: 8 };
const btnDanger = { padding: '10px 16px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' };
const btnFilter = { padding: '8px 12px', background: '#17a2b8', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 };

const getEmptyForm = () => ({
  id: null, level: '', chapter: '',
  question_text: '',
  question_tag_1: '', question_tag_2: '', question_tag_3: '',
  answer_a: '', answer_a_tag1: '', answer_a_tag2: '', answer_a_tag3: '',
  answer_b: '', answer_b_tag1: '', answer_b_tag2: '', answer_b_tag3: '',
  answer_c: '', answer_c_tag1: '', answer_c_tag2: '', answer_c_tag3: '',
  answer_d: '', answer_d_tag1: '', answer_d_tag2: '', answer_d_tag3: '',
  correct_answer: 'A'
});

const API_BASE = process.env.REACT_APP_API_BASE || '/api';

export default function AdminScienceView() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [levelFilter, setLevelFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState(getEmptyForm());
  const [error, setError] = useState('');

  useEffect(() => { fetchQuestions(); }, []);

  const fetchQuestions = async () => {
    setLoading(true); setError('');
    try {
      // ✅ Science API endpoint
      const res = await fetch(`${API_BASE}/admin/questions/science`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      // Apply level filter if selected
      const filtered = levelFilter ? data.filter(q => q.level === levelFilter) : data;
      setQuestions(filtered);
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  // ✅ Re-filter when levelFilter changes (client-side)
  useEffect(() => {
    if (questions.length > 0 && levelFilter) {
      const filtered = questions.filter(q => q.level === levelFilter);
      setQuestions(filtered);
    }
  }, [levelFilter]);

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSave = async () => {
    setError('');
    const method = editingId ? 'PUT' : 'POST';
    // ✅ Science API endpoint
    const url = editingId 
      ? `${API_BASE}/admin/questions/science/${editingId}`
      : `${API_BASE}/admin/questions/science`;
    try {
      const res = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(formData)
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Save failed');
      await fetchQuestions(); setEditingId(null); setShowAddForm(false); setFormData(getEmptyForm());
    } catch (err) { setError(err.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      // ✅ Science API endpoint
      const res = await fetch(`${API_BASE}/admin/questions/science/${id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!res.ok) throw new Error('Delete failed');
      await fetchQuestions();
    } catch (err) { setError(err.message); }
  };

  const clearFilter = () => {
    setLevelFilter('');
    fetchQuestions();
  };

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>Science Questions</h2>
        <button onClick={() => navigate('/admin/dashboard')} style={btnSecondary}>← Back</button>
      </div>

      {error && <div style={{ padding: 10, background: '#f8d7da', color: '#721c24', borderRadius: 6, marginBottom: 15 }}>{error}</div>}

      <button onClick={() => { setShowAddForm(true); setEditingId(null); setFormData(getEmptyForm()); }} style={btnPrimary}>+ Add New Question</button>

      {/* ✅ Level Filter Bar */}
      <div style={{ marginTop: 15, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ fontWeight: 500, fontSize: 14 }}>Filter by Level:</label>
        <select 
          style={{ ...inputStyle, width: 'auto', marginBottom: 0, fontSize: 13, padding: '6px 10px' }}
          value={levelFilter}
          onChange={(e) => setLevelFilter(e.target.value)}
        >
          <option value="">All Levels</option>
          {['1','2','3','4','5','6'].map(l => (
            <option key={l} value={l}>Level {l}</option>
          ))}
        </select>
        {levelFilter && (
          <button onClick={clearFilter} style={btnFilter}>Clear Filter</button>
        )}
        <span style={{ fontSize: 13, color: '#666' }}>
          {questions.length} question{questions.length !== 1 ? 's' : ''} shown
        </span>
      </div>

      {(showAddForm || editingId) && (
        <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd', marginTop: 20 }}>
          <h3 style={{ marginTop: 0 }}>{editingId ? 'Edit' : 'New'} Question</h3>
          <label style={labelStyle}>Level: <input style={inputStyle} name="level" value={formData.level} onChange={handleChange} placeholder="e.g. 1, 2, 3..." /></label>
          
          {/* ✅ Science uses Chapter (same as Math) */}
          <label style={labelStyle}>Chapter: <input style={inputStyle} name="chapter" value={formData.chapter} onChange={handleChange} placeholder="e.g. Biology, Chemistry, Physics..." /></label>
          
          <label style={labelStyle}>Question Text: <textarea style={{ ...inputStyle, minHeight: 60 }} name="question_text" value={formData.question_text} onChange={handleChange} /></label>
          
          <strong style={labelStyle}>Question Tags:</strong>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
            <input style={inputStyle} name="question_tag_1" placeholder="Tag 1" value={formData.question_tag_1} onChange={handleChange} />
            <input style={inputStyle} name="question_tag_2" placeholder="Tag 2" value={formData.question_tag_2} onChange={handleChange} />
            <input style={inputStyle} name="question_tag_3" placeholder="Tag 3" value={formData.question_tag_3} onChange={handleChange} />
          </div>

          {['a', 'b', 'c', 'd'].map(opt => (
            <div key={opt} style={{ marginBottom: 12, padding: 12, background: '#f8f9fa', borderRadius: 6 }}>
              <strong>Answer {opt.toUpperCase()}:</strong>
              <input style={inputStyle} name={`answer_${opt}`} placeholder="Answer text" value={formData[`answer_${opt}`]} onChange={handleChange} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                <input style={inputStyle} name={`answer_${opt}_tag1`} placeholder="Tag 1" value={formData[`answer_${opt}_tag1`]} onChange={handleChange} />
                <input style={inputStyle} name={`answer_${opt}_tag2`} placeholder="Tag 2" value={formData[`answer_${opt}_tag2`]} onChange={handleChange} />
                <input style={inputStyle} name={`answer_${opt}_tag3`} placeholder="Tag 3" value={formData[`answer_${opt}_tag3`]} onChange={handleChange} />
              </div>
            </div>
          ))}

          <label style={labelStyle}>Correct Answer:
            <select style={inputStyle} name="correct_answer" value={formData.correct_answer} onChange={handleChange}>
              <option>A</option><option>B</option><option>C</option><option>D</option>
            </select>
          </label>
          <div style={{ marginTop: 10 }}>
            <button onClick={handleSave} style={btnPrimary}>Save</button>
            <button onClick={() => { setEditingId(null); setShowAddForm(false); }} style={btnSecondary}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ marginTop: 20 }}>
        {loading && <p style={{ textAlign: 'center' }}>Loading...</p>}
        
        {!loading && questions.length === 0 && (
          <p style={{ textAlign: 'center', padding: 20, color: '#666' }}>
            {levelFilter ? `No Level ${levelFilter} questions found.` : 'No questions found. Add one above!'}
          </p>
        )}

        {!loading && questions.map(q => (
          <div key={q.id} style={{ background: '#fff', padding: 16, marginBottom: 12, borderRadius: 8, border: '1px solid #ddd' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                {/* ✅ Science: Show Level + Chapter */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                  <span style={{ 
                    padding: '3px 10px', 
                    background: '#6f42c1', 
                    color: '#fff', 
                    borderRadius: 12, 
                    fontSize: 11, 
                    fontWeight: 600 
                  }}>
                    Lvl {q.level || '—'}
                  </span>
                  <span style={{ fontSize: 12, color: '#666' }}>
                    Chapter: {q.chapter || '—'}
                  </span>
                </div>
                
                <p style={{ margin: '6px 0', fontSize: 15 }}>{q.question_text}</p>
                
                {/* Show question-level tags */}
                {(q.question_tag_1 || q.question_tag_2 || q.question_tag_3) && (
                  <div style={{ fontSize: 12, color: '#6f42c1', marginBottom: 4 }}>
                    <strong>Q-Tags:</strong> {[q.question_tag_1, q.question_tag_2, q.question_tag_3].filter(t => t).join(', ')}
                  </div>
                )}
                
                <div style={{ fontSize: 13, color: '#666' }}>
                  A: {q.answer_a} <span style={{ color: '#999' }}>({q.answer_a_tag1}, {q.answer_a_tag2}, {q.answer_a_tag3})</span><br/>
                  B: {q.answer_b} <span style={{ color: '#999' }}>({q.answer_b_tag1}, {q.answer_b_tag2}, {q.answer_b_tag3})</span><br/>
                  C: {q.answer_c} <span style={{ color: '#999' }}>({q.answer_c_tag1}, {q.answer_c_tag2}, {q.answer_c_tag3})</span><br/>
                  D: {q.answer_d} <span style={{ color: '#999' }}>({q.answer_d_tag1}, {q.answer_d_tag2}, {q.answer_d_tag3})</span>
                </div>
                <span style={{ display: 'inline-block', marginTop: 6, padding: '2px 8px', background: '#28a745', color: '#fff', borderRadius: 4, fontSize: 12 }}>Correct: {q.correct_answer}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <button onClick={() => { setEditingId(q.id); setShowAddForm(false); setFormData({ ...q }); }} style={{ ...btnPrimary, padding: '6px 10px', fontSize: 13 }}>Edit</button>
                <button onClick={() => handleDelete(q.id)} style={{ ...btnDanger, padding: '6px 10px', fontSize: 13 }}>Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}